'use client'

import { useState } from 'react'
import { useLocale } from '@/lib/i18n/use-locale'
import { getCsrfHeaders } from '@/lib/utils/csrf'

interface BulkUrlResult {
  success: boolean
  shortUrl: string
  shortCode: string
  originalUrl: string
  expiresAt: string | null
}

interface BulkUrlError {
  index: number
  url: string
  error: string
}

export function BulkUrlForm() {
  const [urls, setUrls] = useState<string>('')
  const [expiresIn, setExpiresIn] = useState<string>('')
  const [utmSource, setUtmSource] = useState('')
  const [utmMedium, setUtmMedium] = useState('')
  const [utmCampaign, setUtmCampaign] = useState('')
  const [utmTerm, setUtmTerm] = useState('')
  const [utmContent, setUtmContent] = useState('')
  const [utmPreset, setUtmPreset] = useState('manual')
  const { t } = useLocale()

  const normalizeUtmValue = (value: string) => value.replace(/\s+/g, '+')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<BulkUrlResult[] | null>(null)
  const [errors, setErrors] = useState<BulkUrlError[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setResults(null)
    setErrors(null)

    // Parse URLs (one per line)
    const urlList = urls
      .split('\n')
      .map(url => url.trim())
      .filter(url => url.length > 0)

    if (urlList.length === 0) {
      setError('Please enter at least one URL')
      setLoading(false)
      return
    }

    if (urlList.length > 20) {
      setError('Maximum 20 URLs allowed at once')
      setLoading(false)
      return
    }

    const normalizeForCompare = (value: string) => {
      const trimmed = value.trim()
      try {
        const parsed = new URL(trimmed)
        const pathname = parsed.pathname !== '/'
          ? parsed.pathname.replace(/\/+$/u, '')
          : parsed.pathname
        const normalized = `${parsed.protocol}//${parsed.host}${pathname}${parsed.search}${parsed.hash}`
        return normalized.toLowerCase()
      } catch {
        return trimmed.toLowerCase().replace(/\/+$/u, '')
      }
    }

    const seen = new Set<string>()
    const duplicates: string[] = []
    for (const url of urlList) {
      const normalized = normalizeForCompare(url)
      if (seen.has(normalized)) {
        duplicates.push(url)
      } else {
        seen.add(normalized)
      }
    }

    if (duplicates.length > 0) {
      const uniqueDuplicates = Array.from(new Set(duplicates))
      setError(`Duplicate URLs found: ${uniqueDuplicates.slice(0, 5).join(', ')}${uniqueDuplicates.length > 5 ? '...' : ''}`)
      setLoading(false)
      return
    }

    try {
      const response = await fetch('/api/shorten/bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getCsrfHeaders()),
        },
        body: JSON.stringify({
          urls: urlList.map(url => ({
            url,
            expiresIn: expiresIn ? parseInt(expiresIn) : undefined,
            utmSource: utmSource || undefined,
            utmMedium: utmMedium || undefined,
            utmCampaign: utmCampaign || undefined,
            utmTerm: utmTerm || undefined,
            utmContent: utmContent || undefined,
          })),
        }),
      })

      const json = await response.json()

      if (!response.ok) {
        throw new Error(json.error || 'Failed to shorten URLs')
      }

      setResults(json.results)
      setErrors(json.errors || null)

      // Clear input if all successful
      if (json.failed === 0) {
        setUrls('')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const copyAllUrls = () => {
    if (!results) return
    const allUrls = results.map(r => r.shortUrl).join('\n')
    navigator.clipboard.writeText(allUrls)
  }

  return (
    <div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="bulk-urls" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('bulk.urlsLabel')}
          </label>
          <textarea
            id="bulk-urls"
            value={urls}
            onChange={(e) => setUrls(e.target.value)}
            rows={8}
            placeholder="https://example.com/page1&#10;https://example.com/page2&#10;https://example.com/page3"
            className="min-h-[160px] w-full rounded-lg border px-4 py-3 text-sm text-gray-900 shadow-sm outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white font-mono"
            required
          />
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {urls.split('\n').filter(u => u.trim()).length} / 20 {t('bulk.count')}
          </p>
        </div>

        <div>
          <label htmlFor="bulk-expires" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('bulk.expiration')}
          </label>
          <select
            id="bulk-expires"
            value={expiresIn}
            onChange={(e) => setExpiresIn(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          >
            <option value="">Never expire</option>
            <option value="1">1 hour</option>
            <option value="24">24 hours</option>
            <option value="168">7 days</option>
            <option value="720">30 days</option>
          </select>
        </div>

        <details className="border rounded-lg p-4 dark:border-gray-700">
          <summary className="cursor-pointer font-medium text-gray-700 dark:text-gray-300">
            {t('rotator.utmTitle')}
          </summary>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 mb-3">{t('rotator.utmHelp')}</p>
          <div className="space-y-3 mt-3">
            <div>
              <label htmlFor="bulk-utm-preset" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Popular presets
              </label>
              <select
                id="bulk-utm-preset"
                value={utmPreset}
                onChange={(e) => {
                  const value = e.target.value
                  setUtmPreset(value)
                  if (value === 'manual') {
                    setUtmSource('')
                    setUtmMedium('')
                    setUtmCampaign('')
                    setUtmTerm('')
                    setUtmContent('')
                    return
                  }
                  const presets: Record<string, { source: string; medium: string; campaign: string }> = {
                    newsletter: { source: 'newsletter', medium: 'email', campaign: 'monthly-newsletter' },
                    facebook: { source: 'facebook', medium: 'social', campaign: 'social-post' },
                    facebookads: { source: 'facebook', medium: 'paid-social', campaign: 'facebook-ads' },
                    instagram: { source: 'instagram', medium: 'social', campaign: 'social-post' },
                    x: { source: 'x', medium: 'social', campaign: 'social-post' },
                    linkedin: { source: 'linkedin', medium: 'social', campaign: 'social-post' },
                    googleads: { source: 'google', medium: 'cpc', campaign: 'search-ads' },
                    whatsapp: { source: 'whatsapp', medium: 'messaging', campaign: 'whatsapp-share' },
                    telegram: { source: 'telegram', medium: 'messaging', campaign: 'telegram-share' },
                    line: { source: 'line', medium: 'messaging', campaign: 'line-share' },
                    tiktokads: { source: 'tiktok', medium: 'paid-social', campaign: 'tiktok-ads' },
                    tiktoksocial: { source: 'tiktok', medium: 'social', campaign: 'tiktok-social' },
                  }
                  const preset = presets[value]
                  if (preset) {
                    setUtmSource(preset.source)
                    setUtmMedium(preset.medium)
                    setUtmCampaign(preset.campaign)
                    setUtmTerm('')
                    setUtmContent('')
                  }
                }}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              >
                <option value="manual">Manual / None</option>
                <option value="newsletter">Newsletter (email)</option>
                <option value="facebook">Facebook (social)</option>
                <option value="facebookads">Facebook Ads (paid social)</option>
                <option value="instagram">Instagram (social)</option>
                <option value="x">X / Twitter (social)</option>
                <option value="linkedin">LinkedIn (social)</option>
                <option value="googleads">Google Ads (cpc)</option>
                <option value="whatsapp">WhatsApp (messaging)</option>
                <option value="telegram">Telegram (messaging)</option>
                <option value="line">Line Messenger (messaging)</option>
                <option value="tiktokads">TikTok Ads (paid social)</option>
                <option value="tiktoksocial">TikTok Social (social)</option>
              </select>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="bulk-utm-source" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  utm_source
                </label>
                <input
                  type="text"
                  id="bulk-utm-source"
                  value={utmSource}
                  onChange={(e) => setUtmSource(normalizeUtmValue(e.target.value))}
                  placeholder="newsletter"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="bulk-utm-medium" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  utm_medium
                </label>
                <input
                  type="text"
                  id="bulk-utm-medium"
                  value={utmMedium}
                  onChange={(e) => setUtmMedium(normalizeUtmValue(e.target.value))}
                  placeholder="email"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                />
                <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                  Channels: cpc (paid ads), email, social, organic, affiliate, referral
                </p>
              </div>
              <div>
                <label htmlFor="bulk-utm-campaign" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  utm_campaign
                </label>
                <input
                  type="text"
                  id="bulk-utm-campaign"
                  value={utmCampaign}
                  onChange={(e) => setUtmCampaign(normalizeUtmValue(e.target.value))}
                  placeholder="spring-launch"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                />
                <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                  Descriptive names: spring_sale_2026, product_launch_x, newsletter_q1, black_friday
                </p>
              </div>
              <div>
                <label htmlFor="bulk-utm-term" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  utm_term
                </label>
                <input
                  type="text"
                  id="bulk-utm-term"
                  value={utmTerm}
                  onChange={(e) => setUtmTerm(normalizeUtmValue(e.target.value))}
                  placeholder="running-shoes"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                />
                <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                  Target Keywords: running+shoes, marketing+software
                </p>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="bulk-utm-content" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  utm_content
                </label>
                <input
                  type="text"
                  id="bulk-utm-content"
                  value={utmContent}
                  onChange={(e) => setUtmContent(normalizeUtmValue(e.target.value))}
                  placeholder="cta-button"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                />
                <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                  Element descriptors: header_button, text_link, banner_ad_blue, video_ad
                </p>
              </div>
            </div>
          </div>
        </details>

        <button
          type="submit"
          disabled={loading}
          className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
        >
          {loading ? t('bulk.processing') : t('bulk.submit')}
        </button>
      </form>

      {error && (
        <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
        </div>
      )}

      {results && results.length > 0 && (
        <div className="mt-6 p-6 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-green-900 dark:text-green-100">
              Successfully shortened {results.length} URL{results.length > 1 ? 's' : ''}!
            </h3>
            <button
              onClick={copyAllUrls}
              className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition"
            >
              Copy All
            </button>
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {results.map((result, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 p-3 rounded border border-green-200 dark:border-green-700">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{result.originalUrl}</p>
                    <a
                      href={result.shortUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:underline font-semibold break-all"
                    >
                      {result.shortUrl}
                    </a>
                  </div>
                  <button
                    onClick={() => navigator.clipboard.writeText(result.shortUrl)}
                    className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition flex-shrink-0"
                  >
                    Copy
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {errors && errors.length > 0 && (
        <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <h3 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-2">
            {errors.length} URL{errors.length > 1 ? 's' : ''} failed
          </h3>
          <div className="space-y-2">
            {errors.map((err, index) => (
              <div key={index} className="text-sm text-yellow-700 dark:text-yellow-300">
                <span className="font-mono">{err.url}</span>: {err.error}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
